import { FluentButton } from 'owa-fluent-v9-shims';
import { observer } from 'mobx-react-lite';
import { ObservableMap } from 'mobx';
import {
    scheduleCancelButton,
    scheduleDialogHeader,
    returnToSuggestions,
} from './ScheduleDatePicker.locstring.json';
import { Dialog, DialogFooter, DialogType } from '@fluentui/react/lib/Dialog';
import { addDays, addHours, isAfter, OwaDate, startOfDay } from 'owa-datetime';
import { formatFullUserDateTime } from 'owa-datetime-formatters';
import { DateTimePicker } from 'owa-datetimepicker';
import loc from 'owa-localize';
import { scheduleSaveButton } from 'owa-locstrings/lib/strings/schedulesavebutton.locstring.json';
import * as React from 'react';
import * as ReactDOM from 'owa-react-dom';
import { WindowProvider } from '@fluentui/react/lib/WindowProvider';
import { MruCache } from 'owa-mru-cache';
import { observableNow } from 'owa-observable-datetime';
import { wrapInApolloProvider } from 'owa-apollo';
import { logUsage } from 'owa-analytics';

import styles from './ScheduleDatePicker.scss';

interface ScheduleDatePickerOverrideableProps {
    minTime: OwaDate;
    onScheduleItemClicked: (date: OwaDate) => void;
    acceptButtonText?: string;
    snapToInterval?: boolean;
    targetWindow?: Window;
    title?: string;
    SuggestionDatePicker?: React.ElementType;
    selectedTime?: OwaDate;
}

export interface ShowScheduleDatePickerOverrideableProps
    extends ScheduleDatePickerOverrideableProps {
    onClose?: () => void;
}

export interface ScheduleDatePickerProps extends ScheduleDatePickerOverrideableProps {
    onCleanup: () => void;
}

const cachedItems = new MruCache<Object>(3);
cachedItems.initialize(new ObservableMap<string, Object>());

const DatePicker = observer(function DatePicker(props: ScheduleDatePickerProps) {
    const {
        minTime,
        acceptButtonText,
        onCleanup,
        onScheduleItemClicked,
        snapToInterval,
        title,
        SuggestionDatePicker,
        selectedTime,
    } = props;

    const defaultDate = React.useMemo(() => {
        const currentTime = observableNow();
        const today8AM = addHours(startOfDay(currentTime), 8);
        // if minTime is after today8AM, use tomorrow 8AM.
        return isAfter(minTime, today8AM) ? addDays(today8AM, 1) : today8AM;
    }, []);

    const [isAdvanced, setIsAdvanced] = React.useState(false);
    const [scheduledTime, setScheduledTime] = React.useState(isAdvanced ? defaultDate : null);

    const onChanged = (date: OwaDate) => {
        setScheduledTime(date);
    };

    const onScheduleDatePickerSave = React.useCallback(() => {
        setIsAdvanced(false);
        logUsage('ScheduleDatePicker_CustomUsed', [isAdvanced]);
        if (scheduledTime) {
            if (isAdvanced) {
                cachedItems.add(scheduledTime.toString(), {
                    text: formatFullUserDateTime(scheduledTime),
                    date: scheduledTime,
                });
            }
            onScheduleItemClicked(scheduledTime);
        }

        onCleanup();
    }, [onScheduleItemClicked, onCleanup, scheduledTime]);

    const onScheduleDatePickerCancel = React.useCallback(() => {
        setIsAdvanced(false);

        onCleanup();
    }, [onCleanup]);

    const switchAdvanced = () => {
        setScheduledTime(!isAdvanced ? defaultDate : null);
        setIsAdvanced(!isAdvanced);
    };

    return (
        <Dialog
            key="ScheduleDatePicker"
            hidden={false}
            onDismiss={onScheduleDatePickerCancel}
            dialogContentProps={{
                type: DialogType.normal,
                title:
                    title && (!SuggestionDatePicker || !isAdvanced)
                        ? loc(title)
                        : loc(scheduleDialogHeader),
                showCloseButton: true,
                styles: {
                    innerContent: (!SuggestionDatePicker || isAdvanced) && styles.innerContent,
                },
                topButtonsProps:
                    SuggestionDatePicker && isAdvanced
                        ? [
                              {
                                  onClick: switchAdvanced,
                                  iconProps: { iconName: 'Back' },
                                  ariaLabel: loc(returnToSuggestions),
                                  title: loc(returnToSuggestions),
                              },
                          ]
                        : [],
            }}
            styles={{
                main: styles.fixWidthContextMenuSuggestion,
            }}>
            {SuggestionDatePicker && !isAdvanced ? (
                <SuggestionDatePicker
                    onScheduleTimeChanged={setScheduledTime}
                    cachedItems={cachedItems}
                    onAdvancedClick={switchAdvanced}
                />
            ) : (
                <DateTimePicker
                    minDate={minTime}
                    initialValue={selectedTime ?? defaultDate}
                    onChanged={onChanged}
                    showDuration={false}
                    snapToInterval={snapToInterval}
                />
            )}
            <DialogFooter className={styles.footer}>
                <FluentButton
                    appearance="primary"
                    onClick={onScheduleDatePickerSave}
                    className={styles.buttonRadii}
                    disabled={!scheduledTime}
                    text={acceptButtonText ? acceptButtonText : loc(scheduleSaveButton)}
                />
                <FluentButton
                    onClick={onScheduleDatePickerCancel}
                    className={styles.buttonRadii}
                    text={loc(scheduleCancelButton)}
                />
            </DialogFooter>
        </Dialog>
    );
});

export function showScheduleDatePickerDialog(props: ShowScheduleDatePickerOverrideableProps) {
    const {
        minTime,
        onScheduleItemClicked,
        onClose,
        acceptButtonText,
        snapToInterval,
        targetWindow,
        title,
        SuggestionDatePicker,
        selectedTime,
    } = props;
    const dialogDiv = (targetWindow || window).document.createElement('div');
    dialogDiv.id = 'snoozeDatePickerDialog';
    (targetWindow || window).document.body.appendChild(dialogDiv);

    function cleanup() {
        ReactDOM.unmountComponentAtNode(dialogDiv);
        (targetWindow || window).document.body.removeChild(dialogDiv);
        if (onClose) {
            onClose();
        }
    }

    ReactDOM.render(
        wrapInApolloProvider(() => (
            <>
                <WindowProvider window={targetWindow || window}>
                    <DatePicker
                        minTime={minTime}
                        onScheduleItemClicked={onScheduleItemClicked}
                        onCleanup={cleanup}
                        acceptButtonText={acceptButtonText}
                        snapToInterval={snapToInterval}
                        title={title}
                        SuggestionDatePicker={SuggestionDatePicker}
                        selectedTime={selectedTime}
                    />
                    ,
                </WindowProvider>
            </>
        ))(),
        dialogDiv
    );
}
